import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  pickLocale,
  t as translate,
  type Locale,
  type MessageKey,
  type Params,
} from "../../shared/i18n/index.ts";

type Ctx = {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (k: MessageKey, p?: Params) => string;
};

const LocaleContext = createContext<Ctx | null>(null);
const STORAGE_KEY = "yabe.locale";

function initialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "vi" || stored === "en") return stored;
  } catch {
    /* private mode */
  }
  return pickLocale(typeof navigator !== "undefined" ? navigator.language : null);
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* ignore */
    }
  }, []);
  const value = useMemo<Ctx>(
    () => ({ locale, setLocale, t: (k, p) => translate(locale, k, p) }),
    [locale, setLocale],
  );
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useI18n(): Ctx {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useI18n outside LocaleProvider");
  return ctx;
}

export function useT() {
  return useI18n().t;
}
